import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AmenityPicker } from "@/components/AmenityPicker";

export function PropertyEditDialog({ property, open, onClose, onSaved }) {
  const [f, setF] = useState({
    address: property.address || "",
    city: property.city || "",
    rent: property.rent ?? "",
    bedrooms: property.bedrooms ?? 1,
    bathrooms: property.bathrooms ?? 1,
    lease_length_months: property.lease_length_months ?? 12,
    available_date: property.available_date || "",
    description: property.description || "",
    amenities: property.amenities || [],
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF({ ...f, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/properties/${property.id}`, {
        ...f,
        rent: Number(f.rent),
        bedrooms: Number(f.bedrooms),
        bathrooms: Number(f.bathrooms),
        lease_length_months: Number(f.lease_length_months),
      });
      toast.success("Listing updated");
      onSaved?.(data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update listing");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Edit listing</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Address</Label><Input data-testid="edit-address" value={f.address} onChange={(e) => set("address", e.target.value)} /></div>
            <div><Label>City</Label><Input data-testid="edit-city" value={f.city} onChange={(e) => set("city", e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-4 gap-3">
            <div><Label>Rent</Label><Input data-testid="edit-rent" type="number" value={f.rent} onChange={(e) => set("rent", e.target.value)} /></div>
            <div><Label>Beds</Label><Input data-testid="edit-beds" type="number" value={f.bedrooms} onChange={(e) => set("bedrooms", e.target.value)} /></div>
            <div><Label>Baths</Label><Input data-testid="edit-baths" type="number" value={f.bathrooms} onChange={(e) => set("bathrooms", e.target.value)} /></div>
            <div><Label>Lease(mo)</Label><Input data-testid="edit-lease" type="number" value={f.lease_length_months} onChange={(e) => set("lease_length_months", e.target.value)} /></div>
          </div>
          <div><Label>Available date</Label><Input data-testid="edit-date" type="date" value={f.available_date} onChange={(e) => set("available_date", e.target.value)} /></div>
          <div><Label>Description</Label><Textarea data-testid="edit-description" rows={3} value={f.description} onChange={(e) => set("description", e.target.value)} /></div>
          <div>
            <Label>Amenities</Label>
            <div className="mt-2"><AmenityPicker value={f.amenities} onChange={(v) => set("amenities", v)} testidPrefix="edit-amenity" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="edit-save" disabled={saving} onClick={save}>{saving ? "Saving…" : "Save changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
